import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorModeValue,
  Link,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  PHD_STUDENTS,
  MASTERS_STUDENTS,
  POSTDOC_AND_ASSOC,
  NON_RESEARCH_HQP,
  UNDERGRADS,
} from "./AlumniData";

/* --------------------------------
   Small UI building blocks
-------------------------------- */

function HeroHeader({ accent, accent2, softBorder }) {
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");

  const heroBg = useColorModeValue(
    "radial-gradient(1000px 420px at 12% 0%, rgba(34,197,94,0.18), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(22,163,74,0.14), transparent 60%), linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.00))",
    "radial-gradient(1000px 420px at 12% 0%, rgba(34,197,94,0.20), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(22,163,74,0.14), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  );

  return (
    <Box pt={{ base: 8, md: 12 }} pb={{ base: 8, md: 10 }} bg={heroBg} borderBottom="1px solid" borderColor={softBorder}>
      <Container maxW="1200px">
        <HStack spacing={2} flexWrap="wrap">
          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
            border="1px solid"
            borderColor={softBorder}
            fontWeight="800"
          >
            University of Ottawa
          </Tag>

          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("rgba(34,197,94,0.14)", "rgba(34,197,94,0.18)")}
            color={useColorModeValue("#166534", "#86EFAC")}
            fontWeight="900"
          >
            NEXTCON Lab • SCVIC
          </Tag>

          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("rgba(2,6,23,0.04)", "rgba(255,255,255,0.06)")}
            border="1px solid"
            borderColor={softBorder}
            fontWeight="900"
          >
            Alumni / Previous Members
          </Tag>
        </HStack>

        <Heading
          mt={4}
          color={titleColor}
          fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
          lineHeight={1.05}
          letterSpacing="tight"
        >
          Our{" "}
          <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
            Alumni
          </Box>
        </Heading>

        <Text mt={3} color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="85ch" lineHeight={1.8}>
          Graduate students, researchers, and trainees who contributed to NEXTCON Lab & SCVIC.
          Use search to quickly find a name or current affiliation.
        </Text>
      </Container>
    </Box>
  );
}

function SectionBlock({ title, subtitle, children }) {
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const divider = useColorModeValue("rgba(2,6,23,0.08)", "rgba(148,163,184,0.18)");

  return (
    <Box py={{ base: 8, md: 10 }}>
      <Container maxW="1200px">
        <Stack spacing={2} mb={{ base: 5, md: 6 }}>
          <Heading color={titleColor} fontSize={{ base: "xl", md: "2xl" }} letterSpacing="tight">
            {title}
          </Heading>
          {subtitle ? (
            <Text color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="900px">
              {subtitle}
            </Text>
          ) : null}
        </Stack>

        {children}

        <Divider mt={{ base: 10, md: 12 }} borderColor={divider} />
      </Container>
    </Box>
  );
}

function AlumniCard({ item }) {
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const linkColor = useColorModeValue("#16A34A", "#86EFAC");

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={border}
      borderRadius="22px"
      p={{ base: 5, md: 6 }}
      boxShadow={useColorModeValue("0 12px 30px rgba(0,0,0,0.08)", "none")}
      _hover={{
        transform: "translateY(-2px)",
        transition: "160ms ease",
        borderColor: useColorModeValue("rgba(34,197,94,0.55)", "rgba(134,239,172,0.35)"),
      }}
    >
      <Text fontWeight="900" color={titleColor} fontSize={{ base: "md", md: "lg" }} noOfLines={2}>
        {item.name} {item.year ? `(${item.year})` : ""}
      </Text>

      {item.text ? (
        <Text mt={2} color={subColor} lineHeight={1.7}>
          {item.text}
          {item.link ? (
            <>
              {" "}
              (
              <Link href={item.link.href} isExternal color={linkColor} fontWeight="900">
                {item.link.label}
              </Link>
              )
            </>
          ) : null}
        </Text>
      ) : null}

      {item.position ? (
        <Text mt={3} color={subColor} lineHeight={1.7}>
          <Box as="span" fontWeight="900" color={useColorModeValue("#166534", "#86EFAC")}>
            Current Position:
          </Box>{" "}
          <Box as="span" fontStyle="italic">
            {item.position}
          </Box>
        </Text>
      ) : null}
    </Box>
  );
}

function PaginatedList({ title, items, itemsPerPage = 8 }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));
  const start = page * itemsPerPage;
  const slice = items.slice(start, start + itemsPerPage);

  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");

  // clamp if list changes due to search
  React.useEffect(() => {
    if (page > totalPages - 1) setPage(0);
  }, [page, totalPages]);

  return (
    <Box
      bg={panelBg}
      border="1px solid"
      borderColor={border}
      borderRadius="26px"
      p={{ base: 5, md: 6 }}
      boxShadow={useColorModeValue("0 14px 40px rgba(0,0,0,0.10)", "none")}
    >
      <HStack justify="space-between" align="start" flexWrap="wrap" gap={3} mb={4}>
        <Box>
          <Text fontWeight="900" color={titleColor} fontSize={{ base: "lg", md: "xl" }}>
            {title}
          </Text>
          <Text color={subColor} fontSize="sm">
            Showing {Math.min(items.length, start + 1)}–{Math.min(items.length, start + itemsPerPage)} of {items.length}
          </Text>
        </Box>

        <HStack>
          <Button size="sm" variant="outline" borderColor={border} onClick={() => setPage((p) => Math.max(0, p - 1))} isDisabled={page === 0}>
            ◀ Previous
          </Button>
          <Text fontSize="sm" fontWeight="900" color={titleColor}>
            {page + 1} / {totalPages}
          </Text>
          <Button
            size="sm"
            variant="outline"
            borderColor={border}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            isDisabled={page >= totalPages - 1}
          >
            Next ▶
          </Button>
        </HStack>
      </HStack>

      <Stack spacing={4}>
        {slice.map((x, i) => (
          <AlumniCard key={`${title}-${i}`} item={x} />
        ))}
        {items.length === 0 ? (
          <Box py={6}>
            <Text color={subColor}>No matches found.</Text>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}

/* --------------------------------
   Page
-------------------------------- */

export default function Alumni() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);
  const location = useLocation();

  // EN/FR base prefix if you ever need it
  const lang = location.pathname.startsWith("/fr") ? "fr" : "en";
  const base = lang === "fr" ? "/fr" : "/en";
  void base; // keep lint quiet if unused

  // Brand palette
  const accent = "#16A34A";
  const accent2 = "#22C55E";

  const pageBg = useColorModeValue("white", "#0B1220");
  const softBorder = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");

  // Search
  const [q, setQ] = useState("");

  const normalize = (s) => (s || "").toString().toLowerCase();
  const match = (x) => {
    const hay = [
      x?.name,
      x?.text,
      x?.position,
      x?.year,
      x?.link?.label,
      x?.link?.href,
    ]
      .map(normalize)
      .join(" ");
    return hay.includes(normalize(q));
  };

  // Data (filtered)
  const phd = useMemo(() => PHD_STUDENTS.filter(match), [q]);
  const ms = useMemo(() => MASTERS_STUDENTS.filter(match), [q]);
  const postdoc = useMemo(() => POSTDOC_AND_ASSOC.filter(match), [q]);
  const nonResearch = useMemo(() => NON_RESEARCH_HQP.filter(match), [q]);
  const undergrads = useMemo(() => UNDERGRADS.filter(match), [q]);

  return (
    <Box bg={pageBg} minH="100vh">
      <HeroHeader accent={accent} accent2={accent2} softBorder={softBorder} />

      {/* Search bar */}
      <Box py={{ base: 8, md: 10 }}>
        <Container maxW="1200px">
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon opacity={0.65} />
            </InputLeftElement>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search alumni (name, current position, affiliation, year...)"
              borderRadius="18px"
              borderColor={softBorder}
              bg={useColorModeValue("white", "rgba(255,255,255,0.04)")}
              _focus={{ borderColor: "rgba(34,197,94,0.65)", boxShadow: "0 0 0 1px rgba(34,197,94,0.65)" }}
            />
          </InputGroup>
        </Container>
      </Box>

      {/* Graduate */}
      <SectionBlock
        title="Graduate Alumni"
        subtitle="Ph.D. and Master’s alumni listed in chronological order."
      >
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 8 }}>
          <PaginatedList title="Ph.D. Students" items={phd} itemsPerPage={isMobile ? 6 : 8} />
          <PaginatedList title="Master’s Students" items={ms} itemsPerPage={isMobile ? 6 : 8} />
        </SimpleGrid>
      </SectionBlock>

      {/* Postdoc / Associates */}
      <SectionBlock title="Former Postdoctoral & Research Associate Members" subtitle="">
        <Stack spacing={4}>
          {postdoc.map((x, i) => (
            <AlumniCard key={`postdoc-${i}`} item={x} />
          ))}
          {postdoc.length === 0 ? <Text opacity={0.7}>No matches found.</Text> : null}
        </Stack>
      </SectionBlock>

      {/* Non-research HQP */}
      <SectionBlock title="Non-Research HQP" subtitle="">
        <Stack spacing={4}>
          {nonResearch.map((x, i) => (
            <AlumniCard key={`hqp-${i}`} item={x} />
          ))}
          {nonResearch.length === 0 ? <Text opacity={0.7}>No matches found.</Text> : null}
        </Stack>
      </SectionBlock>

      {/* Undergrad */}
      <Box pb={{ base: 12, md: 16 }}>
        <Container maxW="1200px">
          <Stack spacing={2} mb={{ base: 5, md: 6 }}>
            <Heading
              color={useColorModeValue("#0F172A", "white")}
              fontSize={{ base: "xl", md: "2xl" }}
              letterSpacing="tight"
            >
              Former Undergraduate Student Researchers
            </Heading>
          </Stack>

          <Stack spacing={4}>
            {undergrads.map((x, i) => (
              <AlumniCard key={`ug-${i}`} item={x} />
            ))}
            {undergrads.length === 0 ? <Text opacity={0.7}>No matches found.</Text> : null}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}